// Animated radial graph showing the plan tree + sub-agent swarm.
// Pure SVG + framer-motion. Zero canvas dependency.

import { motion, AnimatePresence } from "framer-motion";
import { useMemo } from "react";
import { useAgentSwarm } from "@/hooks/useAgentSwarm";
import { useResearch } from "@/store/researchStore";
import type { PlanNode } from "@/lib/deep-research/types";
import { cn } from "@/lib/utils";

interface Pos { x: number; y: number }

function layoutTree(nodes: Record<string, PlanNode>, rootId: string, w = 320, h = 280): Record<string, Pos> {
  const out: Record<string, Pos> = {};
  // depth -> ordered node ids
  const byDepth: Record<number, string[]> = {};
  const walk = (id: string) => {
    const n = nodes[id];
    if (!n) return;
    (byDepth[n.depth] ||= []).push(id);
    for (const c of n.childIds) walk(c);
  };
  walk(rootId);
  const depths = Object.keys(byDepth).map(Number).sort((a, b) => a - b);
  const maxDepth = depths.length ? depths[depths.length - 1] : 0;
  for (const d of depths) {
    const ids = byDepth[d];
    const r = maxDepth === 0 ? 0 : 0.18 + 0.36 * (d / maxDepth);
    ids.forEach((id, i) => {
      const angle = (i / Math.max(1, ids.length)) * Math.PI * 2 + d * 0.35;
      out[id] = {
        x: w / 2 + r * w * 0.45 * Math.cos(angle),
        y: h / 2 + r * h * 0.45 * Math.sin(angle),
      };
    });
  }
  return out;
}

const STATUS_COLOR: Record<string, string> = {
  pending: "hsl(var(--muted-foreground))",
  running: "hsl(var(--primary))",
  done: "hsl(var(--success))",
  failed: "hsl(var(--destructive))",
  stopped: "hsl(var(--warning))",
};

export function DeepResearchGraph({ height = 280 }: { height?: number }) {
  const { plan, agents } = useAgentSwarm();
  const status = useResearch((s) => s.status);
  const W = 320, H = height;

  const positions = useMemo(() => {
    if (!plan) return {};
    return layoutTree(plan.nodes, plan.rootId, W, H);
  }, [plan]);

  if (!plan) {
    return (
      <div className="rounded-lg border border-border bg-surface-2/40 h-[200px] flex items-center justify-center text-[11px] font-mono text-muted-foreground">
        No active plan — toggle Deep Research and Send.
      </div>
    );
  }

  const root = plan.nodes[plan.rootId];
  const edges: Array<{ from: string; to: string; nodeStatus: string }> = [];
  for (const n of Object.values(plan.nodes)) {
    for (const c of n.childIds) edges.push({ from: n.id, to: c, nodeStatus: plan.nodes[c]?.status ?? "pending" });
  }

  const agentByNode = new Map<string, typeof agents[number]>();
  for (const a of agents) agentByNode.set(a.nodeId, a);

  return (
    <div className="rounded-lg border border-border bg-surface-2/40 overflow-hidden">
      <div className="px-2 py-1 flex items-center justify-between border-b border-border/60">
        <span className="text-[10px] font-mono uppercase tracking-wider text-primary">Plan × Swarm Graph</span>
        <span className="text-[9px] font-mono text-muted-foreground">{Object.keys(plan.nodes).length} nodes · {agents.length} agents · {status}</span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: H }}>
        <defs>
          <radialGradient id="dr-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.45" />
            <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={W / 2} cy={H / 2} r={Math.min(W, H) * 0.45} fill="url(#dr-glow)" />

        {/* edges */}
        {edges.map((e) => {
          const a = positions[e.from];
          const b = positions[e.to];
          if (!a || !b) return null;
          return (
            <motion.line
              key={`${e.from}-${e.to}`}
              x1={a.x} y1={a.y} x2={b.x} y2={b.y}
              stroke={STATUS_COLOR[e.nodeStatus] ?? STATUS_COLOR.pending}
              strokeOpacity={0.45}
              strokeWidth={1}
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 0.55 }}
              transition={{ duration: 0.6 }}
            />
          );
        })}

        {/* nodes */}
        <AnimatePresence>
          {Object.values(plan.nodes).map((n) => {
            const p = positions[n.id];
            if (!p) return null;
            const isRoot = n.id === root.id;
            const ag = agentByNode.get(n.id);
            const r = isRoot ? 9 : ag ? 6 : 4;
            const color = STATUS_COLOR[n.status] ?? STATUS_COLOR.pending;
            return (
              <motion.g
                key={n.id}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 24 }}
              >
                {n.status === "running" && (
                  <motion.circle
                    cx={p.x} cy={p.y} r={r + 4}
                    fill="none" stroke={color} strokeOpacity={0.5} strokeWidth={1}
                    animate={{ r: [r + 2, r + 8, r + 2], opacity: [0.5, 0.1, 0.5] }}
                    transition={{ duration: 1.6, repeat: Infinity }}
                  />
                )}
                <circle cx={p.x} cy={p.y} r={r} fill={color} stroke="hsl(var(--background))" strokeWidth={1} />
                {isRoot && (
                  <text x={p.x} y={p.y - 12} textAnchor="middle" fontSize="8" fill="hsl(var(--foreground))" fontFamily="monospace">root</text>
                )}
              </motion.g>
            );
          })}
        </AnimatePresence>
      </svg>

      {/* legend */}
      <div className="px-2 pb-1.5 pt-1 flex flex-wrap gap-2 text-[9px] font-mono text-muted-foreground">
        {(["pending","running","done","failed","stopped"] as const).map((s) => (
          <span key={s} className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLOR[s] }} />{s}
          </span>
        ))}
      </div>
    </div>
  );
}

export function SwarmAgentList() {
  const { agents } = useAgentSwarm();
  if (!agents.length) return null;
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-mono uppercase tracking-wider text-primary">Sub-Agents ({agents.length})</div>
      <div className="space-y-1 max-h-[160px] overflow-y-auto pr-1">
        {agents.map((a) => (
          <div key={a.id} className={cn(
            "flex items-center gap-2 text-[10px] font-mono p-1.5 rounded border border-border bg-surface-2/60",
            a.status === "done" && "border-success/30",
            a.status === "failed" && "border-destructive/40",
          )}>
            <span
              className={cn(
                "w-1.5 h-1.5 rounded-full shrink-0",
                a.status === "done" ? "bg-success" :
                a.status === "failed" ? "bg-destructive" :
                a.status === "stopped" ? "bg-warning" :
                "bg-primary animate-pulse",
              )}
            />
            <span className="truncate flex-1" title={a.label}>{a.label}</span>
            <span className="text-muted-foreground">{a.iteration}/{a.maxIterations}</span>
            <span className={cn(
              "tabular-nums",
              a.confidence >= 0.75 ? "text-success" :
              a.confidence >= 0.5 ? "text-warning" :
              "text-muted-foreground",
            )}>{Math.round(a.confidence * 100)}%</span>
            <span className="text-muted-foreground">{Math.round(a.latencyMs)}ms</span>
          </div>
        ))}
      </div>
    </div>
  );
}
